import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DragCancelEvent,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { isKeyboardEvent } from '@dnd-kit/utilities';
import {
  IReorderTrainingBlocksBody,
  ITrainingBlock,
} from '../../../models/allenamenti';

/**
 * Solo Spazio afferra/rilascia da tastiera, non Invio: Invio è il tasto con
 * cui BlockCard apre la modal di modifica (composto a mano nel suo
 * `handleKeyDown`, dato che la card è un `<div role="button">` e non un
 * `<button>` nativo — vedi BlockCard). Se intercettassimo anche Invio per il
 * drag, chi naviga da tastiera premendo Invio su una card si troverebbe ad
 * afferrarla invece di aprirla — i due gesti userebbero lo stesso tasto per
 * due azioni diverse.
 */
const KEYBOARD_CODES = {
  start: ['Space'],
  cancel: ['Escape'],
  end: ['Space'],
};

interface IUseWeekBoardDndParams {
  /** Tutti i blocchi della settimana corrente, piatti (non raggruppati per giorno). */
  blocks: ITrainingBlock[];
  /** I sette giorni mostrati, in ordine: servono a riconoscere un drop su colonna vuota. */
  dayIsos: string[];
  /** Persiste la nuova sequenza dei giorni toccati. */
  onReorder: (body: Omit<IReorderTrainingBlocksBody, 'teamId'>) => void;
}

/** Blocco e giorno di destinazione a cui va restituito il fuoco dopo il drop. */
interface IPendingFocus {
  blockId: string;
  targetIso: string;
}

/**
 * Sensori e stato del drag-and-drop dei blocchi della settimana.
 *
 * Copre entrambi i gesti: verticale (riordino dentro lo stesso giorno) e
 * diagonale (altro giorno E altra posizione, nello stesso trascinamento). Per
 * questo le card usano `useSortable` di @dnd-kit/sortable e non più il solo
 * `useDraggable`: serve sapere sopra QUALE card si sta rilasciando, non solo
 * sopra quale colonna.
 *
 * Segue il pattern già in uso in CoachSessionWeekView (stesso DndContext +
 * PointerSensor ad attivazione a distanza), con l'aggiunta del KeyboardSensor
 * richiesto qui per l'accessibilità.
 */
export function useWeekBoardDnd({
  blocks,
  dayIsos,
  onReorder,
}: IUseWeekBoardDndParams) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pendingFocus, setPendingFocus] = useState<IPendingFocus | null>(null);

  // Nodo DOM (il <div role="button"> della card, F1) per ogni blocco montato,
  // registrato da BlockCard tramite registerCardNode. Serve solo a ridare il
  // fuoco dopo un drop da tastiera (vedi effetto sotto) — non ha nulla a che
  // fare con dnd-kit stesso, che tiene la propria mappa separata.
  const cardNodes = useRef(new Map<string, HTMLDivElement>());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { keyboardCodes: KEYBOARD_CODES }),
  );

  const activeBlock = useMemo(
    () => (activeId ? blocks.find((b) => b._id === activeId) ?? null : null),
    [activeId, blocks],
  );

  const registerCardNode = useCallback((id: string, node: HTMLDivElement | null) => {
    if (node) cardNodes.current.set(id, node);
    else cardNodes.current.delete(id);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    // Un nuovo drag rende irrilevante un eventuale ripristino del fuoco
    // ancora in sospeso da un drop precedente.
    setPendingFocus(null);
  }, []);

  /**
   * Calcola la sequenza dei giorni toccati e la manda.
   *
   * `over` può essere due cose diverse: l'id di un GIORNO (rilascio sullo
   * spazio vuoto della colonna) oppure l'id di un BLOCCO (rilascio sopra una
   * card, che è il caso normale quando la colonna non è vuota). Nel secondo
   * caso la posizione di destinazione è quella della card sotto il cursore.
   *
   * Si mandano sempre le sequenze COMPLETE dei giorni coinvolti — uno se il
   * trascinamento è verticale, due se è diagonale — invece di "sposta di N":
   * la scrittura resta idempotente e non esiste uno stato intermedio in cui
   * il blocco è già nel nuovo giorno ma non ancora nella posizione giusta.
   */
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const overId = event.over?.id ? String(event.over.id) : null;
      if (!overId) return;

      const blockId = String(event.active.id);
      const block = blocks.find((b) => b._id === blockId);
      if (!block) return;

      const isDayTarget = dayIsos.includes(overId);
      const overBlock = isDayTarget ? null : blocks.find((b) => b._id === overId);
      // Un `over` che non è né un giorno né un blocco conosciuto non è
      // interpretabile: meglio non scrivere niente che indovinare.
      if (!isDayTarget && !overBlock) return;

      const targetIso = isDayTarget ? overId : (overBlock as ITrainingBlock).localDate;
      const sourceIso = block.localDate;

      const inDay = (iso: string) =>
        blocks
          .filter((b) => b.localDate === iso)
          .sort((a, b) => a.order - b.order)
          .map((b) => b._id);

      const source = inDay(sourceIso).filter((id) => id !== blockId);
      const target = sourceIso === targetIso ? source : inDay(targetIso);

      // Sopra una card prende il suo posto; sullo spazio vuoto della colonna
      // va in fondo, che è dove il gesto "lo butto qui sotto" lo suggerisce.
      const overIndex = overBlock ? target.indexOf(overId) : -1;
      const insertAt = overIndex >= 0 ? overIndex : target.length;
      target.splice(insertAt, 0, blockId);

      // Rilasciato dov'era già: niente è cambiato, nessuna richiesta.
      const before = inDay(sourceIso);
      if (sourceIso === targetIso && before.join() === target.join()) return;

      const days =
        sourceIso === targetIso
          ? [{ localDate: targetIso, blockIds: target }]
          : [
              { localDate: sourceIso, blockIds: source },
              { localDate: targetIso, blockIds: target },
            ];

      onReorder({ days });

      // Il fuoco va ripristinato solo se il drag è partito da tastiera: con
      // il mouse il fuoco dell'utente non era sulla card da spostare, quindi
      // spostarcelo dopo il drop sarebbe una sorpresa, non un aiuto.
      if (isKeyboardEvent(event.activatorEvent)) {
        setPendingFocus({ blockId, targetIso });
      }
    },
    [blocks, dayIsos, onReorder],
  );

  const handleDragCancel = useCallback((_event: DragCancelEvent) => {
    setActiveId(null);
    setPendingFocus(null);
  }, []);

  /**
   * Ripristina il fuoco sulla card appena spostata da tastiera.
   *
   * Non possiamo farlo dentro handleDragEnd: subito dopo il drop la card
   * esiste ancora nel DOM, ma nella colonna VECCHIA — l'update ottimistico
   * che la sposta in quella nuova arriva un tick dopo (onMutate della
   * mutation è asincrono, `await`-a `cancelQueries` prima di scrivere la
   * cache). Se cercassimo il nodo subito, troveremmo quello vecchio, che sta
   * per smontare — o niente, se nel frattempo è già smontato.
   *
   * Aspettiamo quindi che `blocks` rifletta davvero il nuovo `localDate` del
   * blocco spostato: a quel punto React ha già rimontato la card nella
   * colonna di destinazione (stesso commit del cambio dati) e il suo nodo è
   * già registrato in cardNodes tramite registerCardNode, chiamato durante
   * il commit — prima che questo effetto (che gira dopo) venga eseguito.
   */
  useEffect(() => {
    if (!pendingFocus) return;
    const moved = blocks.find((b) => b._id === pendingFocus.blockId);
    // Non ancora aggiornato (rollback in caso di errore incluso): niente da
    // fare, l'effetto ritenterà al prossimo cambio di `blocks`.
    if (!moved || moved.localDate !== pendingFocus.targetIso) return;

    cardNodes.current.get(pendingFocus.blockId)?.focus();
    setPendingFocus(null);
  }, [blocks, pendingFocus]);

  return {
    sensors,
    activeBlock,
    registerCardNode,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
}
